'use client';

// Зачем: P2P-обёртка над MysteryRoomCoreSlice. Хост авторитетен по позициям актёров и шлёт state в DataChannel,
// гости шлют свой input хосту и рендерят остальных как remote-сущности через `localBots`.
// Использует PHP signaling (p2pSignaling.ts) и нативный WebRTC (p2pTransport.ts).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { apiClient } from '@/lib/api';
import { getStoredUserRaw } from '@/lib/authStorage';
import MysteryRoomCoreSlice from './MysteryRoomCoreSlice';
import type { MysteryRoomMapDefinition } from './runtime/world/mapTypes';
import type {
  MysteryRoomHeroState,
  MysteryRoomInventoryItemState,
  MysteryRoomLocalBotState,
  MysteryRoomLocalRuntime,
  MysteryRoomBotDifficulty,
  MysteryRoomGameMode,
  MysteryRoomGlobalMissionKey,
} from './runtime/core/localRuntime';
import type { MysteryRoomP2pHostGuestStateBundle } from './runtime/systems/interactionSystem';
import type { MysteryRoomP2pHostPistolBundle } from './runtime/systems/combatSystem';
import { doorActorsFromP2pGuestShadows, type DoorActor } from './runtime/systems/doorSystem';
import { updateHeroMovement } from './runtime/systems/movementSystem';
import {
  mrP2pStartGuest,
  mrP2pStartHost,
  mrP2pStartHostResume,
  type MrP2PGuestHandle,
  type MrP2PHostHandle,
  type MrP2PInputMessage,
  type MrP2PLightSnapshot,
  type MrP2PMessage,
  type MrP2PNpcSnapshot,
  type MrP2PRemoteActor,
  type MrP2PWorldState,
  type P2pAuthoritativeExtraWorld,
  type P2pHostIncomingAction,
} from './runtime/net/p2pTransport';
import { mrApiLobbyCurrent, mrApiLobbyDisband, mrApiLobbyLeave } from './runtime/net/mysteryRoomApi';
import { mrP2pInfo } from './runtime/net/p2pSignaling';
import { MYSTERY_ROOM_DEFAULT_WANDER_BOTS } from './mysteryRoomWanderDefaults';
import { MR_P2P_SOFT_UNMOUNT_KEY } from './mrP2pSessionFlags';

type Props = {
  matchId?: string;
  mapId: string;
  mapDefinition: MysteryRoomMapDefinition;
  mapTitle: string;
  botCount?: number;
  botsOnly?: boolean;
  gameMode?: MysteryRoomGameMode;
  botDifficulty?: MysteryRoomBotDifficulty;
  globalMission?: MysteryRoomGlobalMissionKey;
  zombieWaveCount?: number;
  zombieWaveSize?: number;
  /** Зачем: хост переходит из MR-лобби с общим кодом — создаём P2P-лобби с этим id, если записи ещё нет. */
  assumeP2pHostBootstrap?: boolean;
  onBackToMenu: () => void;
};

// Зачем: транслируем internal-стейт в формат remote-актёров (тот же тип, что и localBots).
function actorToLocalBot(actor: MrP2PRemoteActor): MysteryRoomLocalBotState {
  return {
    id: actor.peerId,
    name: actor.displayName,
    position: { ...actor.position },
    velocity: { ...actor.velocity },
    facing: actor.facing,
    zoneId: '',
    target: { ...actor.position },
    speed: 110,
    radius: 16,
    scale: 1,
    color: actor.color,
    animationState: actor.animationState,
    decisionAtMs: 0,
    lastAttackAtMs: 0,
    aggression: 1,
    remotePeer: true,
  };
}

const HOST_BROADCAST_INTERVAL_MS = 100;
const GUEST_INPUT_INTERVAL_MS = 80;

function readStoredUserId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = getStoredUserRaw();
  if (!raw) return null;
  try {
    const u = JSON.parse(raw) as { id?: unknown };
    const id = Number(u?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function guestSpawnOffset(peerId: string): { x: number; y: number } {
  let h = 0;
  for (let i = 0; i < peerId.length; i += 1) h = (h * 31 + peerId.charCodeAt(i)) | 0;
  const ring = [
    { x: 0, y: 0 },
    { x: 36, y: 0 },
    { x: -36, y: 0 },
    { x: 0, y: 36 },
    { x: 0, y: -36 },
    { x: 26, y: 26 },
    { x: -26, y: -26 },
    { x: 26, y: -26 },
  ];
  return ring[Math.abs(h) % ring.length];
}

function createGuestShadowHero(template: MysteryRoomHeroState, map: MysteryRoomMapDefinition, peerId: string): MysteryRoomHeroState {
  const off = guestSpawnOffset(peerId);
  return {
    ...template,
    position: { x: map.spawn.x + off.x, y: map.spawn.y + off.y },
    zoneId: map.spawnZoneId,
    velocity: { x: 0, y: 0 },
    movementMode: 'idle',
    animationState: 'idle',
    carriedObjectId: null,
    carriedObjectWeightKg: 0,
    spottedByNpcIds: [],
    alertState: 'clear',
    hidden: false,
    hideZoneId: null,
  };
}

export default function MysteryRoomP2PBeta({
  matchId,
  mapId,
  mapDefinition,
  mapTitle,
  botCount,
  botsOnly,
  gameMode,
  botDifficulty,
  globalMission,
  zombieWaveCount,
  zombieWaveSize,
  assumeP2pHostBootstrap = false,
  onBackToMenu,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const [status, setStatus] = useState('Подготовка P2P-лобби...');
  const [role, setRole] = useState<'host' | 'guest' | null>(null);
  const [lobbyId, setLobbyId] = useState<string>('');
  const [connected, setConnected] = useState(false);
  const [peerCount, setPeerCount] = useState(0);

  const handleRef = useRef<MrP2PHostHandle | MrP2PGuestHandle | null>(null);
  const externalBotsRef = useRef<MysteryRoomLocalBotState[] | null>(null);
  const externalNpcsRef = useRef<MrP2PNpcSnapshot[] | null>(null);
  const externalLightsRef = useRef<MrP2PLightSnapshot[] | null>(null);
  const p2pHostInteractQueueRef = useRef<P2pHostIncomingAction[]>([]);
  const p2pAuthoritativeHeroRef = useRef<{ health: number; maxHealth: number; alive: boolean } | null>(null);
  const p2pLastShotMsByPeerRef = useRef<Map<string, number>>(new Map());
  const p2pHostPistolBundleRef = useRef<MysteryRoomP2pHostPistolBundle | null>(null);
  const guestInventoriesRef = useRef<Map<string, MysteryRoomInventoryItemState[]>>(new Map());
  const guestInventoryReadyRef = useRef<Set<string>>(new Set());
  const p2pAuthoritativeGuestInventoryRef = useRef<MysteryRoomInventoryItemState[] | null>(null);
  const pendingGuestInventorySyncRef = useRef(false);
  const p2pAuthoritativeUsedInteractableIdsRef = useRef<string[] | null>(null);
  const p2pAuthoritativeExtraWorldRef = useRef<P2pAuthoritativeExtraWorld | null>(null);
  // Зачем: авторитет хоста — теневое состояние героя гостя для updateHeroMovement.
  const guestShadowHeroesRef = useRef<Map<string, MysteryRoomHeroState>>(new Map());
  // Зачем: хост хранит вход от гостей (peerId → последний input/позиция), чтобы включать их в state-broadcast.
  const guestInputsRef = useRef<Map<string, { input: MrP2PInputMessage; lastUpdateMs: number; displayName: string; color: number }>>(new Map());
  const p2pHostGuestStateBundleRef = useRef<MysteryRoomP2pHostGuestStateBundle>({
    guestInventories: guestInventoriesRef,
    guestShadowHeroes: guestShadowHeroesRef,
  });
  const p2pHostExtraDoorActorsRef = useRef<DoorActor[]>([]);
  const lastBroadcastMsRef = useRef(0);
  const lastInputSendMsRef = useRef(0);
  const lastSelfStateRef = useRef<{ position: { x: number; y: number }; facing: 'up' | 'down' | 'left' | 'right'; animationState: 'idle' | 'walk' } | null>(null);

  const welcomeSettingsRef = useRef({
    mapId,
    botCount: botCount ?? 0,
    botsOnly: !!botsOnly,
    gameMode: (gameMode ?? 'coop') as MysteryRoomGameMode,
    botDifficulty: (botDifficulty ?? 'mid') as MysteryRoomBotDifficulty,
    globalMission: (globalMission ?? 'evac') as MysteryRoomGlobalMissionKey,
    zombieWaveCount: zombieWaveCount ?? 0,
    zombieWaveSize: zombieWaveSize ?? 0,
  });

  useEffect(() => {
    welcomeSettingsRef.current = {
      mapId,
      botCount: botCount ?? 0,
      botsOnly: !!botsOnly,
      gameMode: (gameMode ?? 'coop') as MysteryRoomGameMode,
      botDifficulty: (botDifficulty ?? 'mid') as MysteryRoomBotDifficulty,
      globalMission: (globalMission ?? 'evac') as MysteryRoomGlobalMissionKey,
      zombieWaveCount: zombieWaveCount ?? 0,
      zombieWaveSize: zombieWaveSize ?? 0,
    };
  }, [mapId, botCount, botsOnly, gameMode, botDifficulty, globalMission, zombieWaveCount, zombieWaveSize]);

  useEffect(() => {
    let mounted = true;
    const start = async () => {
      try {
        const wireHost = (host: MrP2PHostHandle, statusMsg: string) => {
          handleRef.current = host;
          setLobbyId(host.lobbyId);
          setConnected(true);
          setStatus(statusMsg);
          host.onPeerJoin((peerId, displayName) => {
            setPeerCount((n) => n + 1);
            guestInputsRef.current.set(peerId, {
              input: { peerId, move: { x: 0, y: 0 }, pointerTarget: null, tsMs: 0 },
              lastUpdateMs: Date.now(),
              displayName,
              color: pickPeerColor(peerId),
            });
            const w = welcomeSettingsRef.current;
            host.sendTo(peerId, {
              type: 'welcome',
              payload: {
                selfId: peerId,
                hostId: host.hostId,
                mapId: w.mapId,
                botCount: w.botCount,
                botsOnly: w.botsOnly,
                gameMode: w.gameMode,
                botDifficulty: w.botDifficulty,
                globalMission: w.globalMission,
                zombieWaveCount: w.zombieWaveCount,
                zombieWaveSize: w.zombieWaveSize,
              },
            });
          });
          host.onPeerLeave((peerId) => {
            setPeerCount((n) => Math.max(0, n - 1));
            guestInputsRef.current.delete(peerId);
            guestShadowHeroesRef.current.delete(peerId);
            guestInventoriesRef.current.delete(peerId);
            guestInventoryReadyRef.current.delete(peerId);
          });
          host.onMessage((peerId, msg) => handleHostMessage(peerId, msg));
        };

        if (matchId) {
          const sleepMs = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
          const tryInfo = async () => {
            try {
              return await mrP2pInfo(matchId);
            } catch {
              return null;
            }
          };
          const waitForInfo = async (maxMs: number) => {
            const deadline = Date.now() + maxMs;
            while (mounted && Date.now() < deadline) {
              const i = await tryInfo();
              if (i) return i;
              await sleepMs(900);
            }
            return null;
          };
          const wireGuestSession = (guest: Awaited<ReturnType<typeof mrP2pStartGuest>>) => {
            handleRef.current = guest;
            setLobbyId(guest.lobbyId);
            guest.onConnected(() => {
              setConnected(true);
              setStatus('Подключено к хосту');
              pendingGuestInventorySyncRef.current = true;
            });
            guest.onDisconnected((reason) => {
              setConnected(false);
              if (reason === 'connection_failed') {
                setStatus(
                  'P2P: не удалось установить связь (часто жёсткий NAT). Попробуйте TURN на сервере, другую сеть или VPN.',
                );
                return;
              }
              if (reason === 'datachannel_closed') {
                setStatus('Канал данных закрыт. Обновите страницу и зайдите по ссылке лобби снова.');
                return;
              }
              setStatus(`Соединение разорвано: ${reason}`);
            });
            guest.onMessage((msg) => handleGuestMessage(msg));
          };

          let info = await tryInfo();
          if (!mounted) return;
          const myId = readStoredUserId();

          if (info && myId !== null && info.hostUserId === myId) {
            setRole('host');
            setStatus('Восстановление хоста…');
            const host = await mrP2pStartHostResume(matchId);
            if (!mounted) return;
            wireHost(host, `Лобби (хост): ${host.lobbyId}`);
            return;
          }

          if (info) {
            setStatus('Подключение к хосту...');
            setRole('guest');
            try {
              wireGuestSession(await mrP2pStartGuest(matchId));
              return;
            } catch (guestErr) {
              console.warn('[p2p] guest join failed', guestErr);
              const reason = (() => {
                const msg =
                  typeof (guestErr as { message?: unknown })?.message === 'string'
                    ? String((guestErr as { message: string }).message).trim()
                    : '';
                return msg || 'не удалось выполнить join';
              })();
              setRole(null);
              setConnected(false);
              setStatus(`Не удалось подключиться к лобби (${matchId}): ${reason}`);
              return;
            }
          }

          if (assumeP2pHostBootstrap && myId !== null) {
            setRole('host');
            setStatus('Создание общего P2P-лобби…');
            try {
              const host = await mrP2pStartHost({
                lobbyId: matchId,
                mapId,
                botCount: botCount ?? MYSTERY_ROOM_DEFAULT_WANDER_BOTS,
                botsOnly,
                maxPeers: 7,
              });
              if (!mounted) return;
              wireHost(host, `Лобби (хост): ${host.lobbyId}`);
              return;
            } catch (bootstrapErr) {
              const msg =
                bootstrapErr instanceof Error ? bootstrapErr.message : String(bootstrapErr ?? '');
              const exists = /409/.test(msg) && /already_exists/i.test(msg);
              if (!exists) {
                console.warn('[p2p] bootstrap host failed', bootstrapErr);
                if (mounted) setStatus(`Не удалось создать общее лобби: ${msg || 'ошибка signaling'}`);
                setRole(null);
                return;
              }
              info = (await tryInfo()) ?? (await waitForInfo(12_000));
              if (!mounted) return;
              if (info && myId !== null && info.hostUserId === myId) {
                setStatus('Восстановление хоста…');
                const host = await mrP2pStartHostResume(matchId);
                if (!mounted) return;
                wireHost(host, `Лобби (хост): ${host.lobbyId}`);
                return;
              }
            }
          }

          if (!info) {
            setStatus('Ожидаем создания комнаты хостом…');
            info = await waitForInfo(assumeP2pHostBootstrap ? 12_000 : 40_000);
            if (!mounted) return;
          }

          if (!info) {
            setRole(null);
            setConnected(false);
            setStatus(
              `Не удалось подключиться к лобби (${matchId}): хост ещё не поднял сессию или signaling недоступен`,
            );
            return;
          }

          if (myId !== null && info.hostUserId === myId) {
            setRole('host');
            setStatus('Восстановление хоста…');
            const host = await mrP2pStartHostResume(matchId);
            if (!mounted) return;
            wireHost(host, `Лобби (хост): ${host.lobbyId}`);
            return;
          }

          setStatus('Подключение к хосту...');
          setRole('guest');
          try {
            wireGuestSession(await mrP2pStartGuest(matchId));
            return;
          } catch (guestErr) {
            console.warn('[p2p] guest join failed', guestErr);
            const reason = (() => {
              const msg =
                typeof (guestErr as { message?: unknown })?.message === 'string'
                  ? String((guestErr as { message: string }).message).trim()
                  : '';
              return msg || 'не удалось выполнить join';
            })();
            setRole(null);
            setConnected(false);
            setStatus(`Не удалось подключиться к лобби (${matchId}): ${reason}`);
            return;
          }
        }
        if (!mounted) return;
        setStatus('Создание лобби...');
        setRole('host');
        const host = await mrP2pStartHost({
          lobbyId: undefined,
          mapId,
          botCount: botCount ?? MYSTERY_ROOM_DEFAULT_WANDER_BOTS,
          botsOnly,
          maxPeers: 7,
        });
        if (!mounted) return;
        wireHost(host, `Лобби создано: ${host.lobbyId}`);
      } catch (err) {
        console.error('[p2p] start failed', err);
        if (mounted) setStatus(`Ошибка P2P: ${(err as Error).message}`);
      }
    };
    void start();
    return () => {
      mounted = false;
      let soft = false;
      try {
        if (typeof window !== 'undefined') {
          soft = sessionStorage.getItem(MR_P2P_SOFT_UNMOUNT_KEY) === '1';
          sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
        }
      } catch {
        /* */
      }
      const handle = handleRef.current;
      handleRef.current = null;
      if (handle) void handle.close({ destroySignaling: !soft });
    };
  }, [assumeP2pHostBootstrap, matchId, mapId, botCount, botsOnly]);

  // Зачем: если никто не поднял роль хоста (или WebRTC завис), подсказка гостю.
  useEffect(() => {
    if (role !== 'guest' || connected) return;
    const t = window.setTimeout(() => {
      setStatus(
        'Нет ответа хоста. Создатель лобби должен открыть игру под своим аккаунтом (в панели — «Хост»). Гостю после этого: обновите страницу.',
      );
    }, 32000);
    return () => window.clearTimeout(t);
  }, [role, connected]);

  // Зачем: подставляем matchId в URL у хоста — можно обновить страницу или скопировать адрес из адресной строки.
  useEffect(() => {
    if (role !== 'host' || !lobbyId || typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('matchId') === lobbyId) {
      return;
    }
    params.set('mode', 'play');
    params.set('net', 'p2p');
    params.set('matchId', lobbyId);
    params.delete('p2pHost');
    const base = pathname && pathname.startsWith('/') ? pathname : '/games/mystery-room';
    router.replace(`${base}?${params.toString()}`, { scroll: false });
  }, [role, lobbyId, router, pathname]);

  // Зачем: гости получают state — кладут актёров (исключая себя) в externalBotsRef.
  const handleGuestMessage = useCallback((msg: MrP2PMessage) => {
    const handle = handleRef.current;
    if (!handle || handle.role !== 'guest') return;
    if (msg.type === 'welcome') {
      const pl = msg.payload as Record<string, unknown>;
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        params.set('mode', 'play');
        params.set('net', 'p2p');
        params.set('matchId', handle.lobbyId);
        params.delete('p2pHost');
        const welcomeMapId = typeof pl.mapId === 'string' ? pl.mapId : '';
        if (welcomeMapId) {
          params.set('mapId', welcomeMapId);
        }
        const bc = typeof pl.botCount === 'number' ? pl.botCount : undefined;
        if (bc !== undefined && bc >= 0) {
          params.set('bots', String(bc));
        }
        if (pl.botsOnly) {
          params.set('botsOnly', '1');
        } else {
          params.delete('botsOnly');
        }
        const gm = typeof pl.gameMode === 'string' ? pl.gameMode : '';
        if (gm) {
          params.set('gameMode', gm);
        }
        const bd = typeof pl.botDifficulty === 'string' ? pl.botDifficulty : '';
        if (bd) {
          params.set('botDifficulty', bd);
        }
        const gmiss = typeof pl.globalMission === 'string' ? pl.globalMission : '';
        if (gmiss) {
          params.set('globalMission', gmiss);
        }
        const wc = typeof pl.zombieWaveCount === 'number' ? pl.zombieWaveCount : null;
        if (wc !== null && wc > 0) {
          params.set('waveCount', String(wc));
        } else {
          params.delete('waveCount');
        }
        const ws = typeof pl.zombieWaveSize === 'number' ? pl.zombieWaveSize : null;
        if (ws !== null && ws > 0) {
          params.set('waveSize', String(ws));
        } else {
          params.delete('waveSize');
        }
        const base = pathname && pathname.startsWith('/') ? pathname : '/games/mystery-room';
        const nextUrl = `${base}?${params.toString()}`;
        const curUrl = `${window.location.pathname}${window.location.search}`;
        if (nextUrl !== curUrl) {
          router.replace(nextUrl, { scroll: false });
          setStatus('Синхронизация с хостом: карта и параметры лобби');
        }
      }
      pendingGuestInventorySyncRef.current = true;
      return;
    }
    if (msg.type === 'state') {
      const world = msg.payload as MrP2PWorldState;
      externalBotsRef.current = world.actors
        .filter((actor) => actor.peerId !== handle.selfId)
        .map(actorToLocalBot);
      const selfActor = world.actors.find((a) => a.peerId === handle.selfId);
      if (selfActor) {
        p2pAuthoritativeHeroRef.current = {
          health: selfActor.health,
          maxHealth: selfActor.maxHealth,
          alive: selfActor.alive,
        };
      }
      externalNpcsRef.current = world.npcs !== undefined ? world.npcs : null;
      p2pAuthoritativeUsedInteractableIdsRef.current = world.usedInteractableIds ?? [];
      if (world.lights !== undefined) {
        externalLightsRef.current = world.lights.length ? world.lights : null;
      }
      if (world.guestInventories != null && handle.selfId in world.guestInventories) {
        const inv = world.guestInventories[handle.selfId] ?? [];
        p2pAuthoritativeGuestInventoryRef.current = inv.map((it, slotIndex) => ({ ...it, slotIndex }));
      }
      if (
        world.doors !== undefined &&
        world.triggers !== undefined &&
        world.hazards !== undefined &&
        world.worldAlarm !== undefined
      ) {
        p2pAuthoritativeExtraWorldRef.current = {
          doors: world.doors,
          triggers: world.triggers,
          hazards: world.hazards,
          worldAlarm: world.worldAlarm,
        };
      }
    } else if (msg.type === 'match-end') {
      setStatus('Матч завершён хостом');
    }
  }, [mapId, pathname, router]);

  // Зачем: хост принимает ввод гостей и каждый тик прогоняет его через updateHeroMovement на своей карте.
  const handleHostMessage = useCallback((peerId: string, msg: MrP2PMessage) => {
    if (msg.type === 'input') {
      const slot = guestInputsRef.current.get(peerId);
      if (!slot) return;
      slot.input = { ...(msg.payload as MrP2PInputMessage), peerId };
      slot.lastUpdateMs = Date.now();
    } else if (msg.type === 'inventory_init') {
      const pl = msg.payload;
      const items = (pl.items ?? []).map((it, i) => ({ ...it, slotIndex: i }));
      guestInventoriesRef.current.set(peerId, items);
      guestInventoryReadyRef.current.add(peerId);
    } else if (msg.type === 'interact') {
      const pl = msg.payload as { interactableId: string };
      if (pl?.interactableId) {
        p2pHostInteractQueueRef.current.push({ interactableId: pl.interactableId, guestPeerId: peerId });
      }
    } else if (msg.type === 'door') {
      const pl = msg.payload as { doorId: string };
      if (pl?.doorId) {
        p2pHostInteractQueueRef.current.push({ doorId: pl.doorId });
      }
    } else if (msg.type === 'shoot') {
      const pl = msg.payload as { fromPeerId: string; targetX: number; targetY: number; tsMs: number };
      if (Number.isFinite(pl.targetX) && Number.isFinite(pl.targetY)) {
        p2pHostInteractQueueRef.current.push({
          fromPeerId: peerId,
          targetX: pl.targetX,
          targetY: pl.targetY,
          tsMs: pl.tsMs,
        });
      }
    }
  }, []);

  // Зачем: после каждого тика рантайма хост собирает state и шлёт всем; гость шлёт свой input хосту.
  const handleRuntimeTick = useCallback((runtime: MysteryRoomLocalRuntime, dtMs: number) => {
    const handle = handleRef.current;
    if (!handle) return;
    const now = Date.now();
    lastSelfStateRef.current = {
      position: { ...runtime.hero.position },
      facing: runtime.hero.facing,
      animationState: runtime.hero.animationState,
    };
    if (handle.role === 'host') {
      p2pHostPistolBundleRef.current = {
        hostPeerId: handle.hostId,
        guestShadowHeroes: guestShadowHeroesRef,
        lastShotMsByPeer: p2pLastShotMsByPeerRef.current,
      };
      guestInputsRef.current.forEach((slot, pid) => {
        let shadow = guestShadowHeroesRef.current.get(pid);
        if (!shadow) {
          shadow = createGuestShadowHero(runtime.hero, runtime.map, pid);
          guestShadowHeroesRef.current.set(pid, shadow);
        }
        if (!shadow.alive) {
          return;
        }
        const move = slot.input.move ?? { x: 0, y: 0 };
        const input = { move, pointerTarget: slot.input.pointerTarget ?? null };
        const result = updateHeroMovement(runtime.map, shadow, input, runtime.doors, dtMs, []);
        guestShadowHeroesRef.current.set(pid, result.hero);
      });

      p2pHostExtraDoorActorsRef.current = doorActorsFromP2pGuestShadows(
        guestShadowHeroesRef.current,
        guestInventoriesRef.current,
      );

      const peerOverlay: MysteryRoomLocalBotState[] = [];
      guestInputsRef.current.forEach((slot, pid) => {
        const hero = guestShadowHeroesRef.current.get(pid);
        if (!hero) return;
        peerOverlay.push({
          id: pid,
          name: slot.displayName,
          position: { ...hero.position },
          velocity: { ...hero.velocity },
          facing: hero.facing,
          zoneId: hero.zoneId,
          target: { ...hero.position },
          speed: 110,
          radius: 16,
          scale: 1,
          color: slot.color,
          animationState: hero.animationState,
          decisionAtMs: 0,
          lastAttackAtMs: 0,
          aggression: 1,
          remotePeer: true,
        });
      });
      externalBotsRef.current = peerOverlay.length ? peerOverlay : null;

      if (now - lastBroadcastMsRef.current < HOST_BROADCAST_INTERVAL_MS) return;
      lastBroadcastMsRef.current = now;
      const actors: MrP2PRemoteActor[] = [];
      actors.push({
        peerId: handle.hostId,
        displayName: 'Хост',
        isBot: false,
        position: { ...runtime.hero.position },
        facing: runtime.hero.facing,
        velocity: { ...runtime.hero.velocity },
        animationState: runtime.hero.animationState,
        color: 0xf59e0b,
        health: runtime.hero.health,
        maxHealth: runtime.hero.maxHealth,
        alive: runtime.hero.alive,
      });
      guestInputsRef.current.forEach((slot, pid) => {
        const hero = guestShadowHeroesRef.current.get(pid);
        if (!hero) return;
        actors.push({
          peerId: pid,
          displayName: slot.displayName,
          isBot: false,
          position: { ...hero.position },
          facing: hero.facing,
          velocity: { ...hero.velocity },
          animationState: hero.animationState,
          color: slot.color,
          health: hero.health,
          maxHealth: hero.maxHealth,
          alive: hero.alive,
        });
      });
      for (const bot of runtime.localBots ?? []) {
        actors.push({
          peerId: bot.id,
          displayName: bot.name,
          isBot: true,
          position: { ...bot.position },
          facing: bot.facing,
          velocity: { ...bot.velocity },
          animationState: bot.animationState,
          color: bot.color,
          health: 100,
          maxHealth: 100,
          alive: true,
        });
      }
      const npcs: MrP2PNpcSnapshot[] = runtime.npcs.map((n) => ({
        id: n.id,
        position: { ...n.position },
        velocity: { ...n.velocity },
        facing: n.facing,
        animationState: n.animationState,
        alive: n.alive,
        zoneId: n.zoneId,
      }));
      const usedInteractableIds = runtime.interactables.filter((it) => it.state === 'used').map((it) => it.id);
      const lights = runtime.lights.map((l) => ({ id: l.id, enabled: l.enabled }));
      const doors = runtime.doors.map((d) => ({
        id: d.id,
        state: d.state,
        openProgress: d.openProgress,
        holdOpenMs: d.holdOpenMs,
      }));
      const triggers = runtime.triggers.map((t) => ({ id: t.id, lastActivatedAtMs: t.lastActivatedAtMs }));
      const hazards = runtime.hazards.map((h) => ({
        id: h.id,
        kind: h.kind,
        zoneId: h.zoneId,
        position: { ...h.position },
        radius: h.radius,
        intensity: h.intensity,
      }));
      const worldAlarm = {
        state: runtime.worldAlarm.state,
        sourceIncidentId: runtime.worldAlarm.sourceIncidentId,
        activeUntilMs: runtime.worldAlarm.activeUntilMs,
      };
      const guestInventories: Record<string, MysteryRoomInventoryItemState[]> = {};
      guestInventoryReadyRef.current.forEach((pid) => {
        guestInventories[pid] = guestInventoriesRef.current.get(pid) ?? [];
      });
      handle.send({
        type: 'state',
        payload: {
          serverNowMs: now,
          hostId: handle.hostId,
          actors,
          npcs,
          usedInteractableIds,
          lights,
          doors,
          triggers,
          hazards,
          worldAlarm,
          guestInventories,
        },
      });
    } else {
      if (pendingGuestInventorySyncRef.current) {
        pendingGuestInventorySyncRef.current = false;
        handle.send({
          type: 'inventory_init',
          payload: { items: runtime.inventory.map((it) => ({ ...it })), tsMs: now },
        });
      }
      if (now - lastInputSendMsRef.current < GUEST_INPUT_INTERVAL_MS) return;
      lastInputSendMsRef.current = now;
      handle.send({
        type: 'input',
        payload: {
          peerId: handle.selfId,
          move: { ...runtime.input.move },
          pointerTarget: runtime.input.pointerTarget ? { ...runtime.input.pointerTarget } : null,
          facing: runtime.hero.facing,
          position: { ...runtime.hero.position },
          tsMs: now,
        },
      });
    }
  }, []);

  const inviteUrl = useMemo(() => {
    if (!lobbyId) return '';
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    url.searchParams.set('mode', 'play');
    url.searchParams.set('net', 'p2p');
    url.searchParams.set('matchId', lobbyId);
    if (mapId) {
      url.searchParams.set('mapId', mapId);
    }
    return url.toString();
  }, [lobbyId, mapId]);

  const handleCopyInvite = useCallback(() => {
    if (!inviteUrl || typeof navigator === 'undefined') return;
    void navigator.clipboard?.writeText(inviteUrl).catch(() => undefined);
  }, [inviteUrl]);

  // Зачем: хост по выходу шлёт match-end гостям и фиксирует факт матча в leaderboard'е (best-effort).
  const matchStartedAtRef = useRef<number>(Date.now());
  const handleLeaveLobby = useCallback(async () => {
    try {
      if (typeof window !== 'undefined') sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
    } catch {
      /* */
    }
    const handle = handleRef.current;
    const effectiveRole = handle?.role ?? role;
    const elapsedSec = Math.max(0, Math.round((Date.now() - matchStartedAtRef.current) / 1000));
    if (handle?.role === 'host') {
      try {
        handle.send({ type: 'match-end', payload: { reason: 'host_left', results: { elapsedSec } } });
      } catch {}
    }
    try {
      await handle?.close({ destroySignaling: true });
    } catch {
      /* гасим poll/pc без повторной навигации */
    }
    // Зачем: иначе mrApiLobbyCurrent оставляет строку игрока + matched — меню лобби снова включает переход в старую P2P.
    try {
      const cur = await mrApiLobbyCurrent();
      const lid = cur.lobby?.id;
      const myId = readStoredUserId();
      if (typeof lid === 'number' && lid > 0) {
        const imOwner = myId !== null && cur.lobby?.ownerUserId === myId;
        if (imOwner) {
          await mrApiLobbyDisband({ lobbyId: lid });
        } else {
          await mrApiLobbyLeave({ lobbyId: lid });
        }
      }
    } catch {
      /* 401 или уже удалён */
    }
    try {
      await apiClient.request<{ ok?: boolean }>('/api/games/mystery-room/score/', {
        method: 'POST',
        body: JSON.stringify({
          score: elapsedSec,
          net_mode: 'p2p',
          p2p_lobby_id: lobbyId,
          p2p_role: effectiveRole,
          p2p_peer_count: peerCount,
          p2p_session_sec: elapsedSec,
        }),
      });
    } catch {
      // best-effort: статистика — не критична
    }
    onBackToMenu();
  }, [lobbyId, onBackToMenu, peerCount, role]);

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col">
      <div className="absolute right-3 top-3 z-30 flex max-w-md flex-col gap-2 rounded-xl border border-site-warning/40 bg-site-bg/85 p-3 text-xs text-site-warning shadow-xl backdrop-blur">
        <div className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-[0.16em] text-site-warning/80">
          <span>P2P · {role === 'host' ? 'Хост' : role === 'guest' ? 'Гость' : '...'}</span>
          <span className={connected ? 'text-site-success' : 'text-site-warning'}>
            {connected ? 'OK' : '…'}
          </span>
        </div>
        <div>{status}</div>
        {lobbyId && (
          <div className="flex items-center gap-2">
            <code className="truncate rounded bg-site-bg/70 px-2 py-0.5 font-mono">{lobbyId}</code>
            <button
              type="button"
              onClick={handleCopyInvite}
              className="rounded border border-site-warning/40 px-2 py-0.5 text-[11px] uppercase tracking-[0.12em] hover:bg-site-warning/10"
            >
              Скопировать ссылку
            </button>
          </div>
        )}
        {role === 'host' && (
          <div className="text-[11px] text-site-warning/80">Гостей: {peerCount}</div>
        )}
        <button
          type="button"
          onClick={() => void handleLeaveLobby()}
          className="mt-1 rounded bg-site-warning/15 px-2 py-1 text-[11px] uppercase tracking-[0.12em] text-site-warning hover:bg-site-warning/25"
        >
          Выйти из лобби
        </button>
      </div>

      <MysteryRoomCoreSlice
        preferredSeed=""
        mapId={mapId}
        mapDefinition={mapDefinition}
        mapTitle={mapTitle}
        // Зачем: до определения роли при ?matchId= не спавним ИИ (иначе гость на секунду получит «чужих» ботов).
        // Без matchId создатель лобби — хост: сразу передаём botCount с URL (раньше role===null давал 0 и боты никогда не создавались).
        botCount={
          role === 'guest'
            ? 0
            : role === 'host'
              ? (botCount ?? 0)
              : matchId
                ? 0
                : (botCount ?? 0)
        }
        botsOnly={
          role === 'guest' ? false : role === 'host' ? !!botsOnly : matchId ? false : !!botsOnly
        }
        gameMode={gameMode}
        botDifficulty={botDifficulty}
        globalMission={globalMission}
        zombieWaveCount={role === 'host' ? zombieWaveCount : 0}
        zombieWaveSize={role === 'host' ? zombieWaveSize : 0}
        externalBotsRef={externalBotsRef}
        externalNpcsRef={externalNpcsRef}
        externalLightsRef={role === 'guest' ? externalLightsRef : undefined}
        p2pHostInteractQueueRef={role === 'host' ? p2pHostInteractQueueRef : undefined}
        p2pHostPistolBundleRef={role === 'host' ? p2pHostPistolBundleRef : undefined}
        p2pAuthoritativeHeroRef={role === 'guest' ? p2pAuthoritativeHeroRef : undefined}
        p2pIsGuest={role === 'guest'}
        p2pHostGuestStateBundleRef={role === 'host' ? p2pHostGuestStateBundleRef : undefined}
        p2pHostExtraDoorActorsRef={role === 'host' ? p2pHostExtraDoorActorsRef : undefined}
        p2pAuthoritativeGuestInventoryRef={role === 'guest' ? p2pAuthoritativeGuestInventoryRef : undefined}
        onP2pGuestShoot={
          role === 'guest'
            ? (targetX, targetY) => {
                const h = handleRef.current;
                if (h?.role === 'guest') {
                  h.send({
                    type: 'shoot',
                    payload: { fromPeerId: h.selfId, targetX, targetY, tsMs: Date.now() },
                  });
                }
              }
            : undefined
        }
        p2pAuthoritativeUsedInteractableIdsRef={role === 'guest' ? p2pAuthoritativeUsedInteractableIdsRef : undefined}
        p2pAuthoritativeExtraWorldRef={role === 'guest' ? p2pAuthoritativeExtraWorldRef : undefined}
        p2pSkipLocalTriggers={role === 'guest'}
        onP2pGuestInteractableUsed={
          role === 'guest'
            ? (interactableId) => {
                const h = handleRef.current;
                if (h?.role === 'guest') {
                  h.send({ type: 'interact', payload: { interactableId, tsMs: Date.now() } });
                }
              }
            : undefined
        }
        onP2pGuestDoorToggled={
          role === 'guest'
            ? (doorId) => {
                const h = handleRef.current;
                if (h?.role === 'guest') {
                  h.send({ type: 'door', payload: { doorId, tsMs: Date.now() } });
                }
              }
            : undefined
        }
        onRuntimeTick={handleRuntimeTick}
      />
    </div>
  );
}

const PEER_PALETTE = [0x60a5fa, 0xf472b6, 0x34d399, 0xa78bfa, 0xf87171, 0x22d3ee, 0xfbbf24];

function pickPeerColor(peerId: string): number {
  let hash = 0;
  for (let i = 0; i < peerId.length; i += 1) {
    hash = (hash * 31 + peerId.charCodeAt(i)) | 0;
  }
  return PEER_PALETTE[Math.abs(hash) % PEER_PALETTE.length];
}
